import { useAuthStore } from '@/store/auth';
import { useMemo } from 'react';

/**
 * 权限判据。两个口，判据不同：
 *
 * - `canEnter` / `hasMenu`：能不能进这一页。键形是**点分**的 `org.notice` / `ops.site.cfg`，
 *   判据是服务端 `reachableRoutes` 译过来的 `menus`（见 `store/auth.ts` 与 `menuCatalog.ts`）。
 * - `can` / `hasBtn`：能不能点这颗按钮。键形是**点分至少三段**的动作码 `org.notice.draft`，
 *   判据是服务端随登录 / 当前会话下发的动作码集合 `permissions`（岗位 → 权限包 → 码展开后的结果）。
 *   旧冒号式按钮键（`org:info:edit`）已退役：服务端不再下发那种键，传进来按「没有这个码」判
 *   （超管与 `*` 的通配除外）。
 *
 * 两个口都先看 `superAdmin`：服务端判定的「当下有效的内置超管」（内置超管岗位 ＋ 双通配策略行）
 * 为真时一律放行。🔴 **只认服务端那一个布尔**——不按登录邮箱、不按 `pageCount` / `dataScope`，
 * 也不认任何本地标志：都不是「有效内置超管」的判据，推出来的结论还会与网关逐条判的结果对不上。
 * 登录与刷新同源（131 与 132 同一处算），所以刷完页面权限不会缩水。
 *
 * 键形守卫保留：传错键形的判定一定为 false，而且永远不会自己暴露（看起来按钮门控接上了，
 * 其实只是没了）。动作码至少三段（`org.notice.draft`），菜单键两段（`org.notice`）：
 * 正式工作台动作 `home.cfg` 为两段特例；`can` 收到其他两段点分键、或两个口收到已退役的冒号键时，dev 下当场打一条说明键形不对的 console 错误。
 * （三段以上的点分键既可能是菜单键也可能是动作码，形状分不出来，只能按实际集合判。）
 *
 * 也不提供「能不能对这个人按」——那是管理层级，前端手里没有组织架构图算不出来，
 * 由服务端拒绝后按业务码 40305 提示（见 src/constants/bizCode.ts）。
 *
 * ⚠️ 前端这层只防手、不防脚本：真闸是网关与服务端的权限拦截。本 hook 只决定按钮
 * 显不显示、菜单进不进得去，**不是安全边界**。
 */

/** 菜单键的形状：点分（`org.notice`、`member.club.list`） */
const MENU_KEY_SHAPE = /^[a-z0-9]+(\.[a-z0-9]+)+$/;
/** 动作码的形状：点分且至少三段（`org.notice.draft`），见 10-09 第 1.1 节的命名规则 */
const ACTION_KEY_SHAPE = /^[a-z0-9]+(\.[a-z0-9]+){2,}$/;
/**
 * 已退役的冒号式按钮键（`org:info:edit`）。服务端不再下发这种键，留这条只是把误用报出来；
 * 字符类含 `-` 是因为存量里约三成的键带连字符（如 `acct:account:reset-password`）。
 */
const LEGACY_BTN_KEY_SHAPE = /^[a-z0-9]+(:[a-z0-9.-]+)+$/;
/** 服务端给超管下发的通配码：`permissions` 只有它一项时，所有动作都放行（10-09 §5.2） */
const PERMISSION_WILDCARD = '*';

/** dev 下提醒「键形与判定口不匹配」；只在形状明确属于另一类时才报，认不出形状的键不报 */
function warnShape(kind: 'menu' | 'btn', key: string) {
  if (!import.meta.env.DEV) return;
  const caller = kind === 'btn' ? 'can' : 'canEnter';
  if (LEGACY_BTN_KEY_SHAPE.test(key)) {
    console.error(
      `[useAccess] ${caller}("${key}") 收到的是已退役的冒号式按钮键。按钮键已改为点分动作码（如 org.notice.draft），这次判定一定返回 false。`,
    );
    return;
  }
  if (
    kind === 'btn' &&
    key !== 'home.cfg' &&
    MENU_KEY_SHAPE.test(key) &&
    !ACTION_KEY_SHAPE.test(key)
  ) {
    console.error(
      `[useAccess] can("${key}") 收到的是菜单键（点分两段）。动作码至少三段（如 org.notice.draft），这次判定一定返回 false。`,
    );
  }
}

export function useAccess() {
  const status = useAuthStore((s) => s.status);
  const menus = useAuthStore((s) => s.menus);
  const superAdmin = useAuthStore((s) => s.superAdmin);
  const permissions = useAuthStore((s) => s.permissions);
  const scope = useAuthStore((s) => s.scope);

  return useMemo(() => {
    /**
     * 能不能进这一页（菜单键，如 org.notice）。满足其一即可；超管一律能进。
     * 独立设置包授的是动作码、永远不在 menus 里：子页准入的「或」分支请写路由的
     * `handle.actionAccess`（BasicLayout 用 `can` 判），不要塞进这里。
     */
    const canEnter = (menuKey?: string | string[]) => {
      if (superAdmin) return true;
      if (!menuKey) return true;
      const list = Array.isArray(menuKey) ? menuKey : [menuKey];
      return list.some((k) => {
        if (!menus.has(k)) warnShape('menu', k);
        return menus.has(k);
      });
    };
    /** 能不能点这颗按钮（动作码，如 org.notice.draft）：读服务端下发的码集合，满足其一即可 */
    const can = (btnKey?: string | string[]) => {
      if (superAdmin) return true;
      if (!btnKey) return true;
      const list = Array.isArray(btnKey) ? btnKey : [btnKey];
      list.forEach((k) => warnShape('btn', k));
      // `*` 由服务端单独下发（超管岗位），到了前端就是「所有动作都放行」，不逐码展开
      return (
        permissions.has(PERMISSION_WILDCARD) ||
        list.some((k) => permissions.has(k))
      );
    };
    return {
      isLogin: status === 'authed',
      /**
       * 数据范围，服务端 `DataScope` 的 code（all 全部 / dept 本部门 / self 仅本人）。
       * ⚠️ 只是「告知」，不是前端的过滤依据：列表里返回什么行已经由服务端按 DataScopeResolver
       * 决定了，前端拿它只用来告诉用户「你现在看到的是哪一档」（`DataScopeHint`）。
       * 登录出参里没有这一格，所以刚登录时为空串，刷新后才有值。
       */
      scope,
      canEnter,
      can,
      /** 同 {@link canEnter}（dev 侧的叫法） */
      hasMenu: canEnter,
      /** 同 {@link can}（dev 侧的叫法） */
      hasBtn: can,
    };
  }, [status, menus, superAdmin, permissions, scope]);
}

export default useAccess;
